/**
 * E-Commerce & Website — 12 documents.
 *
 * This category ships no templates, and that is deliberate. Privacy, auto-renewal and
 * marketing-consent expectations vary by state and by country and change often, and every one of
 * these documents is a public description of what your business actually does. A policy copied from
 * another brand's site describes practices you do not follow, which is worse than having none —
 * so every entry here is an outline to take to counsel, not a document to publish as it stands.
 */
export const ECOMMERCE_DOCS = [
  {
    id: "website-terms-of-use",
    name: "Website Terms of Use",
    category: "ecommerce",
    subcategory: "Site terms",
    aliases: [
      "Terms of Service",
      "Terms of Use",
      "Website Terms",
      "Site Terms",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Website Visitor",
    whenYouNeedIt:
      "Before the site goes live — as soon as anyone other than you can reach it. Again whenever you add accounts, customer reviews, a loyalty program, a referral tool or anywhere customers upload photos.",
    whatItDoes:
      "Sets the rules for using the website itself: who may use it, what they may do with your content and photography, what happens to the reviews and photos customers post, how you may change the site, and where disputes about the site are handled.",
    whyItMatters:
      "Terms of use govern the site; the sales terms govern the order, and founders routinely publish one and assume it covers both. The gap turns up in ordinary places: a customer photo you want to run as a paid ad with no licence to use it, a review you cannot leave up or take down without an argument, a copied set of terms pointing disputes at another company's home city. None of that is dramatic on any given day, and all of it is expensive to fix once the traffic and the content are already there. There is no template for this in the library because what belongs in it depends on what your site actually does.",
    majorTerms: [
      "Acceptance and how changes to the terms take effect",
      "Eligibility, accounts and account security",
      "Licence to use the site and its content",
      "Licence covering user-generated content: reviews, photos, tags",
      "Prohibited uses: scraping, automated ordering, bulk resale",
      "Intellectual property and permitted brand use",
      "Third-party links, apps and integrations",
      "Disclaimers and limitation of liability",
      "Dispute resolution, venue and governing law",
      "Suspension and termination of access",
      "Accessibility statement",
      "Contact and notice details",
    ],
    clausesToWatch: [
      {
        clause: "User-generated content licence",
        why: "Without a clear licence, the customer photo you want to run as a paid ad or put on packaging is content you do not have permission to use.",
      },
      {
        clause: "How the terms are presented and agreed",
        why: "Terms linked only from a footer, never surfaced at account creation or checkout, are much weaker evidence that anyone agreed to them.",
      },
      {
        clause: "How changes take effect",
        why: "If the document does not say how updates are made and communicated, you have no clean way to show which version a given customer saw.",
      },
      {
        clause: "Dispute resolution and governing law",
        why: "Copied terms usually name a forum, venue or arbitration provider chosen for someone else's business, not yours.",
      },
      {
        clause: "Prohibited uses and resale restrictions",
        why: "Without a term restricting bulk buying for resale, you have no stated basis for declining the orders that reappear on marketplaces at prices you do not set.",
      },
      {
        clause: "Disclaimers and limitation of liability",
        why: "Language written for a software product does not map onto something people swallow, and the mismatch is obvious the first time a claim arrives.",
      },
      {
        clause: "Relationship between the site terms and the sales terms",
        why: "If it is unclear which document governs an order, both get argued over the moment a customer complains.",
      },
    ],
    cpgIssues: [
      "Product claims in site copy, blog posts and recipe content are part of how the product is marketed, and terms of use do nothing to fix a claim problem — the claim itself has to be right.",
      "Unauthorised resellers on marketplaces usually start as ordinary customers buying at retail, and a resale restriction in your terms is the first step toward doing anything about it.",
      "Customer photos of your product at home are the highest-converting creative most small brands ever get, and the licence that lets you run them as ads lives in this document.",
      "Reviews describing health outcomes sit on your site under your name, so whether you can move, edit or remove them is a claims question as much as a terms question.",
      "Ingredient, allergen and nutrition information published on product pages should match the label on the can in the warehouse; when a formula changes, the website is usually the last place anyone updates.",
      "Wholesale enquiries, stockist forms and sample requests often arrive through the same site under consumer-facing terms, even though the relationship is entirely different.",
    ],
    founderMistakes: [
      "Copying a well-known brand's terms of use and publishing them with the other company's name, home city and dispute forum still in the text.",
      "Running a customer's product photo as a paid ad with no licence in the terms and no separate permission, then having to pull the campaign.",
      "Publishing terms in the footer only, never presenting them at checkout, and having nothing to point to when an order was disputed.",
      "Leaving the terms untouched for three years while adding accounts, a subscription and a loyalty program, so the live site and the published terms described different businesses.",
      "Assuming the terms of use covered shipping, refunds and subscription billing, and having no separate policy for any of them.",
    ],
    negotiationPoints: [
      "Ask counsel to draft against what your site actually does today, not against the template you found.",
      "Ask your developer to present the terms at account creation and checkout and to record which version was in force.",
      "Ask for a user-generated content licence broad enough to cover paid social, retailer pages and packaging, if that is how you intend to use customer photos.",
      "Ask counsel where disputes about the site should be handled, and have that reflect your business rather than the one you copied from.",
      "Ask for a documented process for updating the terms and keeping dated versions.",
      "Keep the sales terms, shipping, refund, privacy and subscription policies as separate documents cross-referenced from here.",
    ],
    redFlags: [
      "Terms naming a company, city or forum that is not yours.",
      "No licence at all covering the reviews and photos customers post.",
      "Terms describing features your site does not have, or omitting ones it does.",
      "Terms reachable only from a footer link and never surfaced anywhere else.",
      "A single document trying to serve as terms of use, sales terms, privacy policy and refund policy at once.",
      "Disclaimer language lifted from a software product and applied to a consumable.",
    ],
    beforeYouSign: [
      "A written list of everything your site actually does: accounts, subscriptions, reviews, referrals, wholesale forms, loyalty.",
      "Your separate shipping, refund, privacy and subscription policies, so the terms can point to them rather than duplicate them.",
      "Confirmation from your developer of how and where the terms will be presented and recorded.",
      "A claims review of your site copy, done alongside the terms rather than after them.",
    ],
    related: [
      "terms-and-conditions",
      "privacy-policy",
      "refund-policy",
      "cookie-policy",
      "website-development-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "terms-and-conditions",
    name: "Terms & Conditions",
    category: "ecommerce",
    subcategory: "Site terms",
    aliases: [
      "Terms of Sale",
      "Conditions of Sale",
      "Purchase Terms",
      "Checkout Terms",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Customer",
    whenYouNeedIt:
      "Before you take the first direct order. Again whenever you start selling in a new format — bundles, pre-orders, gift cards, cases through the consumer checkout — because each one changes what you are promising.",
    whatItDoes:
      "Forms the contract for the order itself: price and taxes, when an order is accepted, availability and substitution, delivery and risk of loss, cancellation, and what you owe the customer when something is wrong with the product.",
    whyItMatters:
      "This is the document that decides who pays when a case arrives melted, short or a week late. Most founders publish terms describing an order flow they do not run: pre-orders worded like in-stock product, bundles that can be partly cancelled, a free-shipping threshold that quietly survives a partial refund. Chargebacks are decided on what you published and what your emails said, not on what you meant. Because acceptance, cancellation and consumer-facing terms differ by state and country, this library ships an outline rather than a document.",
    majorTerms: [
      "Order acceptance and when the contract forms",
      "Pricing, taxes and currency",
      "Promotional codes: expiry, stacking, exclusions and thresholds",
      "Availability, backorders and substitution rights",
      "Pre-order handling and stated shipping timeframes",
      "Delivery, title and risk of loss",
      "Cancellation and order changes",
      "Damaged, short or incorrect shipments",
      "Age or eligibility conditions on certain products",
      "Quantity limits and resale restrictions",
      "Gift cards and store credit",
      "Limitation of liability",
      "Governing law and dispute resolution",
    ],
    clausesToWatch: [
      {
        clause: "When the order is accepted",
        why: "If the contract forms the moment payment is captured, a mispriced or out-of-stock item becomes an order you either honour at a loss or cancel awkwardly.",
      },
      {
        clause: "Risk of loss in transit",
        why: "This is the clause that decides who pays for the shipment that arrives ruined, and silence means the argument happens one customer at a time, at your cost.",
      },
      {
        clause: "Promotion code and threshold rules",
        why: "Unstated stacking, expiry and free-shipping rules turn one discount error into a large number of orders you cannot claw back.",
      },
      {
        clause: "Availability and substitution",
        why: "Swapping a flavour or a pack size without a stated right to do so is a shipment the customer can simply reject.",
      },
      {
        clause: "Pre-order language",
        why: "A pre-order described in the same words as in-stock product produces chargebacks the moment the production run slips.",
      },
      {
        clause: "Quantity limits and resale restrictions",
        why: "Without them you have no stated basis for declining the bulk orders that reappear on marketplaces underneath your own pricing.",
      },
      {
        clause: "Limitation of liability",
        why: "Caps drafted for a digital product sit badly on a consumable, and that mismatch is what a customer's bank sees first.",
      },
    ],
    cpgIssues: [
      "Heat, cold and transit time are the defining risk of shipping food, beverage, chocolate and probiotics, so the terms have to say who carries that risk and when you will replace product.",
      "Flavour and pack substitution is routine when a run comes up short, and it needs either a stated right or an approval step rather than a hope the customer will not mind.",
      "Case and pallet quantities bought through a consumer checkout are effectively wholesale orders arriving under a retail contract; the terms should either exclude them or address them directly.",
      "Best-before and use-by dates on shipped product deserve a line: a customer receiving near-date stock from a clearance batch treats it as a defect unless you said so at the point of sale.",
      "Supplement, energy and functional products often carry age or usage conditions the brand itself chooses to apply, and the checkout has to reflect that policy consistently.",
      "Pre-orders are common in CPG precisely because production runs slip, and describing an unmade batch in the language of available stock is the fastest route to disputes.",
    ],
    founderMistakes: [
      "Publishing terms saying the order was accepted at payment, then having to honour a pricing error across hundreds of orders.",
      "Saying nothing about transit temperature, then absorbing the cost of every summer shipment that arrived soft or separated.",
      "Selling a pre-order run in the same language as in-stock product and taking a wave of chargebacks when the co-packer slipped six weeks.",
      "Leaving promotion stacking rules unstated and watching a launch code get combined into free product.",
      "Taking pallet enquiries through the consumer checkout with consumer terms attached, and having no wholesale terms at all.",
    ],
    negotiationPoints: [
      "Ask counsel to write the acceptance clause around your real fulfilment flow, including when you capture payment and when you ship.",
      "State explicitly how heat- and cold-damaged shipments are handled, and hold that answer consistently across email, checkout and policy pages.",
      "Set quantity limits and a resale restriction if unauthorised marketplace listings matter to you.",
      "Separate the pre-order terms and label pre-order items clearly at the point of sale.",
      "Write the promotion rules — expiry, stacking, exclusions, thresholds — before you run the first code, not after.",
      "Keep wholesale terms somewhere other than the consumer checkout.",
    ],
    redFlags: [
      "Terms that never say when an order is accepted.",
      "Silence on transit damage for a temperature-sensitive product.",
      "Refund, shipping and subscription language duplicated here and contradicting the standalone policies.",
      "Pre-order items described identically to in-stock items.",
      "Terms copied from a business that does not ship physical goods.",
      "Language you have never once applied, which makes every other term look optional too.",
    ],
    beforeYouSign: [
      "Your real fulfilment flow written out: when payment is captured, when the order ships, what happens on a partial.",
      "Your carrier's transit times and your product's tolerance for them, tested in the season you actually ship.",
      "The promotion mechanics you intend to run over the next year.",
      "Your separate shipping, refund and subscription policies, so nothing is stated twice in conflicting words.",
    ],
    related: [
      "website-terms-of-use",
      "shipping-policy",
      "refund-policy",
      "subscription-terms",
      "ecommerce-fulfillment-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "privacy-policy",
    name: "Privacy Policy",
    category: "ecommerce",
    subcategory: "Privacy and data",
    aliases: [
      "Privacy Notice",
      "Privacy Statement",
      "Data Protection Notice",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Website Visitors and Customers",
    whenYouNeedIt:
      "Before the site collects anything at all, and an email capture pop-up counts. Again every time you add a tool: a pixel, a chat widget, a loyalty app, a quiz, an SMS platform or a new advertising integration.",
    whatItDoes:
      "Tells people what personal information you collect, how you collect it, why you use it, who else receives it, how long you keep it, and how they can make requests about it.",
    whyItMatters:
      "A privacy policy is the one document on your site that is meant to be a description of your actual practices rather than a set of promises about the future. A copied policy describing practices you do not follow is worse than no policy, because it is a published account of what you do that does not match what you do — and every app, pixel and integration added by whoever was running that week's campaign widens the gap. Expectations here vary by state and by country and change often, which is exactly why this library ships no template for it.",
    majorTerms: [
      "Categories of information collected",
      "Where each category comes from",
      "The purpose of each use",
      "Third parties and processors that receive data",
      "Advertising and analytics tracking",
      "Retention periods you actually enforce",
      "Individual rights and how requests are handled",
      "Position on children's data",
      "International transfers, if you ship or market abroad",
      "Security practices described accurately",
      "Effective date and version history",
      "A contact route for privacy requests that someone owns",
    ],
    clausesToWatch: [
      {
        clause: "Whether the policy describes your actual stack",
        why: "A policy that omits your pixels, chat widget, review app or SMS platform is a public statement that does not match what the site is doing.",
      },
      {
        clause: "Sharing for advertising purposes",
        why: "Routine ad integrations move data in ways a founder does not think of as sharing, and the policy has to describe what actually happens rather than what feels intuitive.",
      },
      {
        clause: "Retention periods",
        why: "Stating a retention period nobody enforces creates an obligation you are already failing on the day you publish it.",
      },
      {
        clause: "Rights request handling",
        why: "Publishing a request route with nobody behind it produces requests you cannot answer inside the time you promised.",
      },
      {
        clause: "Third-party processors",
        why: "Your 3PL, email platform, subscription app and ad tools all receive customer data, and the contracts behind them matter as much as the policy text.",
      },
      {
        clause: "Children's data",
        why: "Brands with bright packaging, candy or character partnerships attract audiences that raise this question whether or not you targeted them.",
      },
      {
        clause: "Effective date and versioning",
        why: "Without dated versions you cannot show what the policy said at the time of a particular customer's order.",
      },
    ],
    cpgIssues: [
      "Quizzes that recommend a supplement, a dose or a regimen collect answers people treat as health information, which raises the stakes on how they are stored and who sees them.",
      "Subscription programs hold payment credentials and purchase history for years, a far longer and richer record than a single DTC order.",
      "Loyalty programs, referral tools and review platforms each add a processor, and each is usually installed by marketing without the policy being touched.",
      "Retailer and marketplace channels sometimes offer customer data under their own terms, and folding that into your own list is not automatically permitted.",
      "Sampling events and trade shows collect contact details on paper that end up in the same list as web sign-ups, under a policy that only ever described the website.",
      "Customer-list matching with ad platforms moves purchase history to third parties in a way a generic policy will not describe at all.",
    ],
    founderMistakes: [
      "Copying a policy from another brand's site and publishing a description of data practices they had never adopted.",
      "Adding a pixel, a chat tool and a review app across a year without touching the policy once.",
      "Publishing a privacy contact address nobody monitored, then finding a backlog of unanswered requests.",
      "Uploading a customer list to an ad platform without the policy ever having described that.",
      "Collecting emails on paper at sampling events and merging them into the marketing list with no record of what people were told.",
    ],
    negotiationPoints: [
      "Ask counsel to write the policy from an inventory of your real tools rather than from a template.",
      "Build that inventory first: every app, pixel, platform and integration touching customer data, and what each one receives.",
      "Name the person inside the company who owns privacy requests, and give that route a real inbox.",
      "Ask counsel which channels the policy needs to cover — website, SMS, events, wholesale — since they collect differently.",
      "Set a review trigger tied to adding a tool rather than to the calendar.",
    ],
    redFlags: [
      "A policy naming another company, another platform, or tools you do not use.",
      "No effective date, or one years old while the site has changed repeatedly.",
      "Described practices — retention, deletion, request handling — that nobody in the company performs.",
      "A privacy request route with no owner.",
      "Nothing said about advertising and analytics on a site that plainly runs both.",
      "One policy claimed to cover every state and country you ship to, with no counsel involvement at all.",
    ],
    beforeYouSign: [
      "A written inventory of every tool, app, pixel and platform that touches customer or visitor data.",
      "A list of the states and countries you actually ship to and market into.",
      "The name of the person who will handle privacy requests and the inbox they will use.",
      "Your SMS, email, loyalty and subscription flows described accurately, since each collects differently.",
    ],
    related: [
      "cookie-policy",
      "sms-marketing-terms",
      "email-marketing-consent",
      "website-terms-of-use",
      "subscription-terms",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "This is a public description of your real data practices, and what it must contain varies by state and country and changes often — it is not a document to adapt from another brand's site.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "shipping-policy",
    name: "Shipping Policy",
    category: "ecommerce",
    subcategory: "Fulfilment and returns",
    aliases: [
      "Delivery Policy",
      "Shipping Information",
      "Shipping & Delivery Terms",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Customer",
    whenYouNeedIt:
      "Before the first order ships, and again before your first summer and your first holiday peak — both of which will break a policy written in a mild month.",
    whatItDoes:
      "Tells customers what shipping costs, how quickly you process and dispatch, which carriers and services you use, where you ship, and what happens when a shipment is late, lost, refused or arrives damaged.",
    whyItMatters:
      "For food, beverage and supplement brands the shipping policy is a product-quality document wearing a logistics label. A summer shipment of chocolate or probiotics that arrives melted or heat-stressed is not a delivery problem, it is a product problem, and if the policy is silent on temperature the customer files a dispute and you have nothing to point at. The same is true of a two-day product handed to a service that will sit in a trailer over a weekend. Write this one against the worst week of your year, not the average one.",
    majorTerms: [
      "Processing time and transit time, stated separately",
      "Shipping rates, thresholds and how they apply to partial orders",
      "Carriers and service levels offered",
      "Destinations served and destinations excluded",
      "Order cut-off times and non-shipping days",
      "Cold-chain, insulated packaging and seasonal handling",
      "Weather holds and the right to delay a dispatch",
      "Address errors, refusals and undeliverable shipments",
      "Lost and delayed shipment process",
      "Damaged-on-arrival process and the evidence you need",
      "Signature or age-check requirements where used",
      "Wholesale and pallet shipments, if handled differently",
    ],
    clausesToWatch: [
      {
        clause: "Whether heat and cold damage is covered",
        why: "This single line decides who pays for every warm-weather shipment that arrives soft, separated or spoiled.",
      },
      {
        clause: "Processing time stated separately from transit time",
        why: "Combining them makes every co-packer or 3PL delay look like a carrier failure, and you refund for something the carrier did not do.",
      },
      {
        clause: "Free-shipping thresholds after a partial refund or return",
        why: "Unstated, a customer can return down below the threshold and keep the shipping you paid to give away.",
      },
      {
        clause: "Lost package process",
        why: "With no stated waiting period and claim route, every scan that stalls becomes an immediate replacement at your cost.",
      },
      {
        clause: "Refused and undeliverable shipments",
        why: "Perishable product returned to a warehouse is usually destroyed, so who pays needs deciding before it happens rather than after.",
      },
      {
        clause: "Seasonal shipping holds",
        why: "A right to hold shipments through a heat wave protects the product, but only if customers were told about it before they ordered.",
      },
      {
        clause: "Carrier delivery promises repeated as your own",
        why: "Restating a carrier's service guarantee in your policy converts their failure into your refund.",
      },
    ],
    cpgIssues: [
      "Chocolate, gummies, coated products and anything with a low melt point behave differently in a trailer than in a warehouse, so the policy has to reflect the season you actually ship in.",
      "Probiotics, live-culture products and some supplements are heat-sensitive in ways a customer cannot see on arrival, which is why the complaint arrives days later.",
      "Insulated liners, gel packs and cold-chain shipping cost real money per order and should be priced into your rates rather than absorbed silently.",
      "Beverages are heavy and dimensional, so a flat rate or free-shipping threshold borrowed from a snack brand loses money on every case.",
      "Glass and cans fail differently — glass breaks, cans dent and leak — and each needs its own packaging standard and damage evidence.",
      "Short-coded or clearance stock shipped to consumers should be labelled as such at the point of sale, because near-date product reads as a defect to the person who opens the box.",
      "3PL cut-offs, pick errors and weekend gaps cause most late shipments, so what you promise has to match what your 3PL contract actually commits to.",
    ],
    founderMistakes: [
      "Publishing a policy silent on heat, then absorbing every melted summer order and the chargebacks that followed.",
      "Promising two-day delivery on a service the 3PL could not dispatch in time, and refunding shipping on most orders in peak week.",
      "Setting a free-shipping threshold copied from a competitor without modelling the weight of a case of beverage.",
      "Not stating a lost-package waiting period and replacing shipments that turned up two days later.",
      "Shipping perishable product to an address the customer typed wrong, and paying for both the loss and the replacement.",
      "Never testing a shipment in July, so the first heat failures were discovered by customers.",
    ],
    negotiationPoints: [
      "State processing time and transit time separately, and set processing against what your 3PL actually commits to.",
      "Say plainly how heat and cold damage is handled, including any seasonal hold, and apply it the same way every time.",
      "Price cold-chain packaging into the shipping rate or the product price rather than absorbing it order by order.",
      "Set a lost-package waiting period and a damage claim window, with the evidence you need named.",
      "Make address errors and refusals the customer's cost where the product cannot be resold.",
      "Check what your 3PL and carrier agreements promise before you promise anything faster.",
    ],
    redFlags: [
      "No mention of temperature at all for a heat-sensitive product.",
      "Delivery promises never confirmed with your 3PL or carrier.",
      "A free-shipping threshold set below the true cost of shipping a case.",
      "No damage or lost-shipment process, so each case is decided ad hoc by whoever answers the email.",
      "Policy language that contradicts your refund policy on damaged goods.",
      "Copied text referencing carriers, regions or services you do not use.",
    ],
    beforeYouSign: [
      "Your 3PL's contracted cut-off and dispatch times, in writing.",
      "Real landed shipping costs by zone for your actual case weight and dimensions.",
      "A summer transit test on your most heat-sensitive product, to the slowest zone you serve.",
      "Your refund policy, read alongside this one so the damaged-goods treatment matches.",
    ],
    related: [
      "refund-policy",
      "terms-and-conditions",
      "ecommerce-fulfillment-agreement",
      "three-pl-agreement",
      "carrier-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "refund-policy",
    name: "Refund Policy",
    category: "ecommerce",
    subcategory: "Fulfilment and returns",
    aliases: [
      "Returns Policy",
      "Return & Refund Policy",
      "Satisfaction Guarantee Terms",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Customer",
    whenYouNeedIt:
      "Before the first order, and again before you advertise any kind of satisfaction guarantee — because from that moment the guarantee is the policy.",
    whatItDoes:
      "Sets what you will refund, replace or credit, within what window, on what evidence, whether product has to come back, and how a refund interacts with shipping charges, discounts, bundles and subscriptions.",
    whyItMatters:
      "This is the document a payment processor reads when a customer disputes a charge, and the one your support inbox is judged against every day. For consumables a physical return is usually pointless — you cannot resell opened food — so the real policy is about replacement, credit and evidence, and one copied from a clothing retailer describes a process you will never run. Where the policy is silent, the customer's bank decides, and it does not decide in your favour.",
    majorTerms: [
      "What qualifies: damaged, incorrect, late, or simply unsatisfied",
      "Claim window measured from delivery",
      "Evidence required, including photographs and lot codes",
      "Whether product must be returned, destroyed or kept",
      "Refund, replacement or store credit, and who chooses",
      "Treatment of shipping charges on a refund",
      "Restocking or handling charges, if any",
      "Effect on bundles, discounts and free-shipping thresholds",
      "Refund method and timing",
      "Subscription orders and how they are treated differently",
      "Wholesale and marketplace orders, excluded or handled separately",
      "The exact wording of any advertised guarantee",
    ],
    clausesToWatch: [
      {
        clause: "Whether opened or partly consumed product qualifies",
        why: "For a consumable this is the entire policy, and if you have advertised a guarantee you have already answered it in public.",
      },
      {
        clause: "Evidence required for a damage claim",
        why: "Without photographs, lot codes and a stated window you cannot tell a genuine transit failure from a repeat claimant.",
      },
      {
        clause: "Whether shipping is refunded",
        why: "Silence means the customer expects it back, and on a heavy product the outbound freight is most of the margin.",
      },
      {
        clause: "Interaction with bundles and thresholds",
        why: "Refunding one item out of a bundle can drop the order below a free-shipping threshold you already paid to honour.",
      },
      {
        clause: "Advertised guarantee wording",
        why: "A marketing promise stated more generously than the policy page is the version the customer and their bank will hold you to.",
      },
      {
        clause: "Subscription refunds and cut-offs",
        why: "A shipment already picked under a subscription is where most cancellation arguments turn into chargebacks.",
      },
      {
        clause: "Claim window length",
        why: "A window shorter than the time it takes heat damage to be noticed produces disputes you lose anyway, having looked unhelpful in the process.",
      },
    ],
    cpgIssues: [
      "Returned food, beverage and supplement product cannot go back into saleable inventory, so a returns process built around physical return is a cost with no recovery attached.",
      "Photograph-and-destroy is the practical mechanic for most consumable claims, and it is worth writing down rather than improvising customer by customer.",
      "Lot codes collected on claims let you tell one bad pallet from a pattern across a whole run, which is worth far more than the refund itself.",
      "Heat-damaged product often looks fine and tastes wrong, so an evidence standard built purely on photographs will miss real failures.",
      "Satisfaction guarantees are common in supplements and functional beverages and are routinely advertised in far broader terms than the policy page says.",
      "Repeat claimants are a real cost in DTC consumables, and a policy with no limit and no record keeping invites them.",
      "Subscription boxes and multi-packs mean one claim can cover several units at once, so a per-order cap matters more here than in single-item categories.",
    ],
    founderMistakes: [
      "Advertising a money-back promise in ad copy while the policy page said returns only, and losing every dispute on the difference.",
      "Building a returns process for a product that cannot be resold, and paying return freight on goods that went straight to disposal.",
      "Refunding outbound shipping by default on a heavy product, turning a small claim rate into a real margin problem.",
      "Not collecting lot codes on damage claims, so a bad run was only spotted when a retailer complained months later.",
      "Cancelling a subscription after the order had already shipped and refunding it anyway, with no stated cut-off.",
      "Having no claim window at all, and receiving claims months after delivery with no way to assess them.",
    ],
    negotiationPoints: [
      "Write the policy around replacement, credit and photograph-and-destroy rather than physical returns.",
      "Require lot codes and photographs on every damage claim, and keep the records where quality can see them.",
      "State clearly whether shipping is refunded, and give the same answer every time.",
      "Set the claim window against how long a real defect takes to surface for your product.",
      "Make your advertised guarantee and your policy page say the same thing, in the same words.",
      "Set a subscription cut-off after which an already-picked order is not refundable, and show it at the moment of cancellation.",
    ],
    redFlags: [
      "A returns process copied from a durable-goods retailer and applied to a consumable.",
      "Advertised guarantee language broader than the published policy.",
      "No claim window, no evidence standard and no record of past claims.",
      "Outbound shipping refunded silently on a heavy or cold-chain product.",
      "Support answering by email in terms the policy page does not support.",
      "No stated treatment of subscription orders, which is where most disputes begin.",
    ],
    beforeYouSign: [
      "Your actual claim rate and its causes, if you have any trading history.",
      "Every place a guarantee is advertised — ads, packaging, landing pages — collected and matched against the policy.",
      "The full cost of a replacement shipment including freight, so the policy is affordable at your real claim rate.",
      "Your subscription platform's cut-off timing, so the cancellation rule matches what the system actually does.",
    ],
    related: [
      "shipping-policy",
      "terms-and-conditions",
      "subscription-terms",
      "auto-renewal-terms",
      "ecommerce-fulfillment-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "subscription-terms",
    name: "Subscription Terms",
    category: "ecommerce",
    subcategory: "Subscriptions and billing",
    aliases: [
      "Subscribe & Save Terms",
      "Membership Terms",
      "Recurring Order Terms",
      "Continuity Program Terms",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Subscriber",
    whenYouNeedIt:
      "Before you switch on a subscribe-and-save option, a membership or any recurring shipment — including the quiet subscribe-and-save toggle sitting on a product page.",
    whatItDoes:
      "Sets how the recurring program works: what each shipment contains, at what price, on what cadence, how the price can change, how shipments are skipped, paused or edited, how a subscriber cancels, and what happens to an order already in progress.",
    whyItMatters:
      "Subscription revenue is the most valuable revenue a small CPG brand can build and the fastest to turn into a complaints problem. Cancellation friction is the specific mechanism: a subscriber who cannot find the cancel button files a chargeback instead, and a pattern of chargebacks threatens the payment account the whole business runs through. Programs also drift — the discount changes, the cadence changes, the pack size changes — and the published terms are the last thing anyone edits. Requirements around recurring billing vary by state and country, so treat this outline as the brief for counsel.",
    majorTerms: [
      "What each shipment contains and how it can change",
      "Cadence options and how a subscriber chooses",
      "Subscriber price and how it differs from the one-off price",
      "Price change process and notice period",
      "Billing date and when the card is actually charged",
      "Skip, pause and edit rights, and their cut-offs",
      "Cancellation method and when it takes effect",
      "Treatment of a shipment already picked or dispatched",
      "Failed payment and retry handling",
      "Any minimum commitment and what ending it early costs",
      "First-box discounts, gifts and whether they claw back",
      "Your right to change or end the program itself",
    ],
    clausesToWatch: [
      {
        clause: "How cancellation actually works",
        why: "Any route harder than the one used to sign up produces disputes and chargebacks regardless of what the terms page says.",
      },
      {
        clause: "The gap between billing and dispatch",
        why: "That gap is where nearly every cancellation argument happens, and if it is undefined you will lose those arguments individually.",
      },
      {
        clause: "Price change notice",
        why: "A rise applied to a card already on file with no notice is the single most common trigger for a subscription complaint.",
      },
      {
        clause: "Minimum commitment",
        why: "An introductory offer carrying a multi-shipment obligation disclosed only in the terms is remembered as a trap whatever the wording said.",
      },
      {
        clause: "First-box discount clawback",
        why: "Charging back an introductory discount on early cancellation surprises people and rarely recovers what it costs in goodwill and disputes.",
      },
      {
        clause: "Failed payment retries",
        why: "Repeated retries against a declined card look like unauthorised charges to the customer and to their bank.",
      },
      {
        clause: "Your right to change contents or cadence",
        why: "Substituting a flavour or shrinking a pack without a stated right turns every supply hiccup into a cancellation event.",
      },
    ],
    cpgIssues: [
      "Consumption rate is the whole design problem: a monthly cadence on a product people use twice a week builds a cupboard full of stock and then a cancellation.",
      "Supplement and functional beverage subscriptions are usually sold on a results narrative, so cancellations cluster at the same point in every cohort when results feel slow.",
      "Flavour rotation and out-of-stocks mean subscription shipments often cannot be identical, so substitution rights and advance notice have to be built in.",
      "Shelf life sets a floor under cadence: shipping faster than people consume leaves subscribers holding product that goes out of date in their own kitchen.",
      "Subscription forecasts drive co-packer runs and packaging orders, so a badly designed cadence produces churn and obsolete inventory at the same time.",
      "Heat-sensitive subscription shipments land on whatever day the cadence produces, including the hottest week of the year, unless the program can hold them.",
      "Retail and marketplace availability undercuts the subscription price the moment the product is discounted elsewhere, which is exactly when subscribers look at the renewal.",
    ],
    founderMistakes: [
      "Switching on subscribe-and-save with the platform's default terms and never writing a policy of their own.",
      "Setting a monthly cadence on a product that lasts two months, and losing subscribers to their own stockpile.",
      "Raising the subscription price with no notice and taking a wave of chargebacks instead of orderly cancellations.",
      "Making cancellation an email request when sign-up was one click, and watching the payment processor flag the dispute rate.",
      "Charging cards and picking orders in the same batch, so a same-day cancellation was impossible to honour.",
      "Running a heavy first-box discount with a clawback nobody remembered agreeing to.",
    ],
    negotiationPoints: [
      "Ask counsel to review the whole flow — sign-up, confirmation, reminders, account page, cancellation — not just the terms text.",
      "Make cancellation available in the same place and by the same method as sign-up.",
      "Send a pre-billing reminder with a real window to skip, edit or cancel.",
      "Set the cadence from consumption data and let subscribers change it in one step.",
      "Give notice of any price change with a chance to cancel before it applies.",
      "Define the cut-off between billing and dispatch, and show it at the moment someone tries to cancel.",
      "Document what your subscription platform actually does at each step and match the terms to it.",
    ],
    redFlags: [
      "Cancellation that requires contacting a human when sign-up did not.",
      "No pre-billing reminder of any kind.",
      "Price changes applied to stored cards with no notice.",
      "A minimum commitment disclosed in the terms but not at sign-up.",
      "Terms describing a program the platform is not configured to run.",
      "A rising chargeback rate treated as a customer-service problem rather than a program design problem.",
    ],
    beforeYouSign: [
      "A written description of exactly what your subscription platform does at each step, from sign-up to cancellation.",
      "Consumption data, or an honest estimate of how long one shipment actually lasts.",
      "Your current chargeback and dispute rate, if you have trading history.",
      "Screenshots of the entire customer-facing flow, so counsel reviews what the subscriber sees rather than what you intended.",
    ],
    related: [
      "auto-renewal-terms",
      "refund-policy",
      "terms-and-conditions",
      "privacy-policy",
      "email-marketing-consent",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Recurring-billing disclosure and cancellation expectations vary by state and country and change often, and the exposure sits in the sign-up and cancellation flow as much as in the words on the terms page.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "auto-renewal-terms",
    name: "Auto-Renewal Terms",
    category: "ecommerce",
    subcategory: "Subscriptions and billing",
    aliases: [
      "Automatic Renewal Disclosure",
      "Recurring Billing Disclosure",
      "Negative Option Terms",
    ],
    stages: ["launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Subscriber",
    whenYouNeedIt:
      "Whenever a customer's card will be charged again without them doing anything further: a subscription, a membership, an annual plan, or a free or discounted trial that converts.",
    whatItDoes:
      "Covers the disclosure and consent mechanics around automatic charges — what is presented before the customer agrees, what is confirmed afterwards, what reminders go out, and how the charges are stopped.",
    whyItMatters:
      "Auto-renewal is the part of a subscription program that is least about the policy page and most about the screen a customer sees at checkout. What must be disclosed, how prominently, what has to be confirmed afterwards and how cancellation must work all differ by state and by country, and they change. A brand shipping nationally is running one checkout flow against many different sets of expectations, which is why this belongs with counsel rather than in a template — and why the fix is almost always in the flow, not the wording.",
    majorTerms: [
      "What is presented before consent, and where on the page",
      "The consent action itself and how it is recorded",
      "Post-purchase confirmation of the renewal terms",
      "Renewal price and how a change is communicated",
      "Renewal date and reminder timing",
      "Cancellation route and effective date",
      "Free or discounted trial conversion mechanics",
      "Records kept of what each customer saw and agreed to",
      "Handling of stored payment credentials",
      "How a materially changed program is re-disclosed to existing subscribers",
    ],
    clausesToWatch: [
      {
        clause: "What the customer actually sees before consenting",
        why: "The checkout screen, not the terms page, is what a dispute gets judged against.",
      },
      {
        clause: "Whether consent is recorded per customer",
        why: "Without a record of the version and the screen a given customer saw, you cannot show what was disclosed to them.",
      },
      {
        clause: "Trial conversion",
        why: "A trial that converts to a full-price recurring charge with no clear reminder is the classic source of complaints and disputes.",
      },
      {
        clause: "Cancellation effective date",
        why: "Ambiguity about whether cancelling stops the next charge or the current one produces refunds and chargebacks in equal measure.",
      },
      {
        clause: "Re-disclosure when the program changes",
        why: "Existing subscribers agreed to the old program, and a materially different one usually needs to be put in front of them again.",
      },
      {
        clause: "Reminder timing",
        why: "A reminder that arrives after the charge is not a reminder, and its absence is the most common complaint pattern in the category.",
      },
      {
        clause: "Stored card handling",
        why: "Charging a card saved for a one-off order as though it were a standing authorisation is where unauthorised-charge disputes start.",
      },
    ],
    cpgIssues: [
      "CPG subscriptions convert through the same checkout as one-off orders, so the renewal disclosure has to work inside a flow designed for a single purchase.",
      "A deeply discounted first box is the standard CPG acquisition offer and the standard renewal complaint, because the second charge is often much larger than the first.",
      "Trial-size and sample offers that convert into full-size recurring shipments change both the price and the product, which is precisely when disputes spike.",
      "Subscription pricing undercut by a retailer promotion makes subscribers examine the next renewal closely and dispute it if the original disclosure was thin.",
      "Seasonal products and limited flavour runs mean the renewing shipment may not be the item the subscriber signed up for, which needs saying up front.",
      "Renewal disputes drive the chargeback rate that your payment processing depends on, which is a larger exposure than the refunds themselves.",
    ],
    founderMistakes: [
      "Relying on the subscription app's default checkout copy and never looking at what the customer actually saw.",
      "Running a heavily discounted first box with the renewal price disclosed only on a linked terms page.",
      "Sending no renewal reminder at all, then treating the resulting disputes as a support problem.",
      "Keeping no record of which disclosure version each subscriber agreed to.",
      "Changing price, size and cadence for existing subscribers by way of a single marketing email.",
      "Converting a trial at full price with no reminder and putting the payment account's standing at risk.",
    ],
    negotiationPoints: [
      "Ask counsel to review the live checkout, the confirmation email and the cancellation flow, not only the written terms.",
      "Capture and store what each subscriber was shown and when they consented.",
      "Send a renewal reminder far enough ahead that a customer can actually act on it.",
      "Disclose the post-discount renewal price on the same screen as the introductory offer.",
      "Make the cancellation effective date something you can state in one sentence.",
      "Re-present the terms to existing subscribers whenever the program materially changes.",
    ],
    redFlags: [
      "Renewal price disclosed only behind a link.",
      "No record of consent beyond the order itself.",
      "No renewal reminders at all.",
      "A trial that converts silently into a different product at a different price.",
      "A cancellation effective date nobody in the company can explain.",
      "Disclosure copy supplied by a platform and never reviewed for the places you actually ship to.",
    ],
    beforeYouSign: [
      "Screenshots of the whole flow: offer, checkout, confirmation email, account page and cancellation.",
      "A list of every state and country you accept subscription orders from.",
      "Your subscription platform's documentation on what consent data it stores and exports.",
      "Your current dispute and chargeback rate on renewing charges specifically.",
    ],
    related: [
      "subscription-terms",
      "refund-policy",
      "terms-and-conditions",
      "privacy-policy",
      "sms-marketing-terms",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "What must be disclosed before an automatic charge, and how cancellation must be offered, vary by state and country and change often — the exposure lives in the checkout flow rather than the policy text.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sms-marketing-terms",
    name: "SMS Marketing Terms",
    category: "ecommerce",
    subcategory: "Marketing consent",
    aliases: [
      "Text Message Marketing Terms",
      "SMS Program Terms",
      "Mobile Messaging Terms",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + SMS Program Subscriber",
    whenYouNeedIt:
      "Before you send a single marketing text — including the first campaign run by a platform that assures you it is all handled for you.",
    whatItDoes:
      "Describes the text-message program: what messages are sent, how often, what it may cost the recipient, how someone joins, how they stop, and how they get help. Behind it sits the consent capture and the records that prove it.",
    whyItMatters:
      "SMS is the highest-response channel most small CPG brands have and the one with the least margin for error. Consent to receive marketing texts is treated as a separate, distinct permission from an email sign-up: a customer who gave you an email address has not agreed to be texted, and a single checkbox covering both is where brands get into difficulty. Platforms sell compliant-looking templates, but the list you imported, the consent you captured and the messages you actually send remain yours. Standards here differ by jurisdiction and change, so the program belongs with counsel before the first send.",
    majorTerms: [
      "Program description and the types of message sent",
      "Message frequency",
      "Cost disclosure to the recipient",
      "How consent is captured and recorded at each entry point",
      "Keywords for stopping and for getting help",
      "Confirmation message on joining",
      "Age or eligibility conditions",
      "Carrier and delivery disclaimers",
      "Data handling and the link to your privacy policy",
      "Rules for list imports from any other source",
      "Sending windows and quiet hours",
      "Program changes and termination",
    ],
    clausesToWatch: [
      {
        clause: "How consent is captured",
        why: "The sign-up unit itself is what matters, and one checkbox covering email and SMS together weakens both permissions at once.",
      },
      {
        clause: "List imports",
        why: "Moving an old email list, an event sign-up sheet or a list from a previous platform into an SMS tool is the most common way brands end up messaging people who never agreed.",
      },
      {
        clause: "Stop and help handling",
        why: "Opt-outs not honoured immediately and across every tool you use are the fastest route to a complaint that sticks.",
      },
      {
        clause: "Message frequency",
        why: "A stated frequency you exceed during a launch is a promise broken in the most visible way available to you.",
      },
      {
        clause: "Sending windows",
        why: "A promotional text at an inconvenient hour generates complaints out of all proportion to the revenue it produces.",
      },
      {
        clause: "Platform responsibility",
        why: "A messaging platform's assurances do not move responsibility for your list and your sends off you.",
      },
      {
        clause: "Consent records",
        why: "Without a record of when and how each number opted in, you cannot answer a complaint at all.",
      },
    ],
    cpgIssues: [
      "Sampling events, trade shows and giveaway entries collect phone numbers on paper or a tablet, and moving those into an SMS platform is a consent question every single time.",
      "Contest and sweepstakes entry forms often bundle a marketing permission into the entry, which is exactly the bundling that causes trouble later.",
      "Product-drop and restock alerts feel operational to a founder and read as marketing to the person receiving them, and the difference matters for consent.",
      "Subscription reminders, shipping notifications and promotional offers frequently run through one SMS tool, so the basis for each type of message has to be kept straight.",
      "Retail partners and distributors sometimes offer to share consumer contact details, which their own customers did not agree to.",
      "Abandoned-cart texting is standard practice in DTC CPG and is marketing whatever the platform calls it in the dashboard.",
    ],
    founderMistakes: [
      "Importing an email list into an SMS platform and texting people who had only ever given an email address.",
      "Using one checkbox at checkout to capture both email and SMS consent.",
      "Bundling an SMS permission into a giveaway entry form and messaging every entrant afterwards.",
      "Exceeding the stated message frequency through a launch week, generating opt-outs and complaints together.",
      "Relying entirely on a platform's compliance assurances and keeping no consent records of their own.",
      "Collecting numbers on a clipboard at a sampling event and adding them to the marketing list months later.",
    ],
    negotiationPoints: [
      "Ask counsel to review the actual consent capture — the sign-up unit and its exact wording — before the first send.",
      "Keep SMS consent separate from email consent, captured separately and recorded separately.",
      "Keep an exportable record of when, where and how each number joined.",
      "Confirm what your platform stores about consent, and what it does not.",
      "Set a message frequency you can hold through a launch, and plan launches inside it.",
      "Never import a list from another channel or platform without counsel looking at it first.",
    ],
    redFlags: [
      "A single consent covering both email and text messages.",
      "Any list import from an email tool, an event sign-up sheet or a purchased source.",
      "No consent records you could produce if asked.",
      "Opt-outs honoured in one tool but not in the others that can send.",
      "Program terms supplied by a platform and never read against what you actually send.",
      "Real sending frequency far above what the program describes.",
    ],
    beforeYouSign: [
      "A screenshot of the exact sign-up unit and its wording, everywhere it appears.",
      "Your platform's documentation on what consent data it records and can export.",
      "A list of every source you have ever collected phone numbers from, including offline ones.",
      "Your privacy policy, updated to describe the SMS program before it launches.",
    ],
    related: [
      "email-marketing-consent",
      "privacy-policy",
      "subscription-terms",
      "sweepstakes-rules",
      "contest-rules",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Consent standards for text-message marketing are distinct from email, vary by jurisdiction and change often, and the exposure attaches to the list and the sends rather than to the terms page.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "email-marketing-consent",
    name: "Email Marketing Consent Language",
    category: "ecommerce",
    subcategory: "Marketing consent",
    aliases: [
      "Email Opt-In Language",
      "Newsletter Sign-Up Terms",
      "Marketing Permission Language",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Email Subscriber",
    whenYouNeedIt:
      "Before the first campaign, and again whenever you add a new place people can join the list: a pop-up, a giveaway, an event clipboard, a wholesale enquiry form or a retailer partnership.",
    whatItDoes:
      "Sets the wording and mechanics of how people join your marketing list, what they are agreeing to, how that agreement is recorded, and how they leave. It is a few lines of text with a records process behind it.",
    whyItMatters:
      "The email list is usually the most valuable asset a DTC CPG brand builds, and it is also the one most often assembled from sources that never agreed to marketing at all. A list made of event sign-ups, giveaway entries, wholesale enquiries and a purchased file is not one list, it is four, and only some of them consented. The cost shows up as collapsing deliverability, a suspended sending platform and complaint rates you cannot explain, almost always in the week you most need the channel.",
    majorTerms: [
      "The consent wording used at each capture point",
      "Whether consent is separate from placing an order or entering a promotion",
      "Confirmation and welcome sequence",
      "Records of source, date and wording against each contact",
      "Unsubscribe mechanics and how fast they take effect",
      "Preference and frequency options",
      "The line between transactional and marketing messages",
      "Rules for imported, inherited or partner-supplied lists",
      "Suppression lists and retention",
      "Link to your privacy policy",
      "How consent is re-confirmed for dormant contacts",
    ],
    clausesToWatch: [
      {
        clause: "Whether marketing consent is bundled with something else",
        why: "Permission collected as a side effect of placing an order or entering a giveaway is weak and easy to challenge when a complaint arrives.",
      },
      {
        clause: "Records of source and wording",
        why: "Without knowing where a contact came from and what they were told, you cannot clean the list or answer a complaint about it.",
      },
      {
        clause: "Transactional versus marketing messages",
        why: "Order and shipping notifications are not marketing, and blurring promotional content into them undermines both categories.",
      },
      {
        clause: "Unsubscribe handling across tools",
        why: "An unsubscribe honoured in one platform but not another is the complaint that keeps arriving from the same person.",
      },
      {
        clause: "Imported and acquired lists",
        why: "A list bought, inherited from an agency or handed over by a partner did not agree to hear from you.",
      },
      {
        clause: "Dormant contacts",
        why: "Mailing addresses that have not engaged for years drives the complaint rate that can cost you the sending platform entirely.",
      },
    ],
    cpgIssues: [
      "Sampling events and farmers markets collect addresses on paper, and whatever the sign-up card said is the only record of what those people agreed to.",
      "Giveaway and contest entries produce large numbers of addresses at once, and entering a giveaway is not the same as asking to hear from a brand every week.",
      "Wholesale and stockist enquiry forms collect business addresses that end up in a consumer marketing list under consumer consent wording.",
      "Retailer, distributor and marketplace partners sometimes offer customer contacts, which those customers did not give to you.",
      "Product withdrawal and safety notices need a route to reach buyers regardless of marketing preferences, which is a different mechanism from the newsletter.",
      "Back-in-stock alerts, launch notifications and subscription reminders each rest on a different basis and are usually run out of one tool by one person.",
    ],
    founderMistakes: [
      "Merging four years of event clipboards, giveaway entries and wholesale enquiries into one list and mailing all of it before a launch.",
      "Buying a list to accelerate a launch and losing the sending platform account instead.",
      "Using a pre-ticked box, or no box at all, on a checkout that also created a marketing subscriber.",
      "Keeping no record of where contacts came from, so a deliverability problem could not be traced or cleaned.",
      "Putting promotional content inside order-confirmation emails and blurring the two categories permanently.",
      "Mailing a dormant list in launch week and destroying deliverability at the worst possible moment.",
    ],
    negotiationPoints: [
      "Ask counsel for consent wording appropriate to each place you collect, including offline capture.",
      "Store source, date and wording against every contact, and confirm your platform can export it.",
      "Keep marketing consent separate from placing an order or entering a promotion.",
      "Run unsubscribes and suppression across every tool that can send, not only the main one.",
      "Set a rule for dormant contacts and apply it before a launch rather than after.",
      "Keep a separate mechanism for reaching buyers about product safety.",
    ],
    redFlags: [
      "Any purchased, scraped or partner-supplied list.",
      "Consent captured as a pre-ticked box or as a by-product of another action.",
      "No record of where contacts came from.",
      "Unsubscribes honoured in one tool but not another.",
      "A giveaway entry list mailed as though it were a newsletter list.",
      "A rising complaint rate treated as a deliverability problem rather than a consent problem.",
    ],
    beforeYouSign: [
      "Every sign-up point you operate, online and offline, with the wording each one uses.",
      "An export from your email platform showing what source and consent data it actually holds.",
      "Your privacy policy, describing the email program accurately.",
      "A written rule for what happens to giveaway, event and wholesale contacts.",
    ],
    related: [
      "sms-marketing-terms",
      "privacy-policy",
      "cookie-policy",
      "sweepstakes-rules",
      "product-giveaway-rules",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Marketing-consent standards differ by country and by channel and change often, and a list assembled from mixed sources is something counsel should look at before a launch rather than after a complaint.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "affiliate-disclosure",
    name: "Affiliate Disclosure",
    category: "ecommerce",
    subcategory: "Disclosures",
    aliases: [
      "Affiliate Link Disclosure",
      "Advertising Disclosure",
      "Material Connection Disclosure",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Site Visitors, and Brand + Affiliates",
    whenYouNeedIt:
      "When your own site earns commission on links out, when you publish content promoting other brands, and when you run an affiliate program whose partners publish about you.",
    whatItDoes:
      "States plainly, where readers actually see it, that links or recommendations may earn a commission — and sets the standard your affiliates are held to when they promote your product on their own channels.",
    whyItMatters:
      "Disclosure is cheap when it is designed in and expensive when it is retrofitted across a few hundred partner sites. For a brand running an affiliate program the real exposure is not your own footer, it is what partners write on their own pages and feeds while pointing at your product, and the highest-earning partners are usually the most aggressive. Expectations around disclosing a paid relationship differ by country and platform and change, so the workable move is a written standard inside the affiliate terms plus an actual check on what partners publish.",
    majorTerms: [
      "Where the disclosure appears and how prominently",
      "Wording used on your own content",
      "The standard imposed on affiliates in the program terms",
      "Platform-specific placement expectations",
      "Monitoring and audit of affiliate content",
      "Consequences for non-disclosure, including withheld commission",
      "Prohibited claims list for affiliates",
      "Trademark and creative asset use rules",
      "Coupon site and paid-search conduct",
      "Records of what partners published and when",
      "Program changes and notice to partners",
    ],
    clausesToWatch: [
      {
        clause: "Where the disclosure sits relative to the link",
        why: "A disclosure at the bottom of a page or behind another link is easy to miss, which defeats the entire point of having one.",
      },
      {
        clause: "The standard imposed on affiliates",
        why: "If the program terms are silent on disclosure, several hundred partners each decide for themselves what is good enough.",
      },
      {
        clause: "Prohibited claims for affiliates",
        why: "An affiliate writing a health outcome you would never publish yourself is still a statement made to sell your product.",
      },
      {
        clause: "Monitoring obligations",
        why: "A written rule nobody checks is a policy in name only, and non-compliance concentrates in your top earners.",
      },
      {
        clause: "Enforcement and removal rights",
        why: "Without a stated right to require edits, withhold commission or terminate, you have no lever when a partner will not comply.",
      },
      {
        clause: "Paid-search and coupon-code conduct",
        why: "Partners bidding on your own brand terms or scraping codes take commission on sales you already had.",
      },
    ],
    cpgIssues: [
      "Supplement, functional beverage and wellness affiliates gravitate toward health and outcome claims because those convert, which is precisely the content the brand cannot stand behind.",
      "Before-and-after and personal-results formats are common in the category and raise disclosure and claim questions in the same post.",
      "Affiliates promoting ingestible products often describe dosing, timing and stacking in ways that go well beyond the label.",
      "Discount-code affiliates feeding marketplace resellers undercut your retail partners and your own subscription pricing at the same time.",
      "Recipe and lifestyle content featuring your product blurs editorial and paid content, which is where disclosure most often goes missing.",
      "Retailer and marketplace affiliate programs stack on top of yours, so a single post can carry two commercial relationships needing disclosure.",
    ],
    founderMistakes: [
      "Launching an affiliate program with a commission structure and no content standard whatsoever.",
      "Discovering that top-earning affiliates were making health claims the brand had spent real money keeping off its own packaging.",
      "Publishing a disclosure only in a site footer while affiliate links ran throughout the content.",
      "Never keeping copies of what affiliates published, so there was no record when a claim surfaced later.",
      "Letting affiliates bid on the brand name in paid search and paying commission on traffic that was already theirs.",
    ],
    negotiationPoints: [
      "Put the disclosure standard in the affiliate program terms, in plain wording partners can copy exactly.",
      "Give affiliates approved copy and an explicit list of claims they may not make.",
      "Reserve the right to require edits, withhold commission and terminate for non-compliance.",
      "Monitor your highest-earning partners, since that is where the exposure concentrates.",
      "Keep dated records of what was published.",
      "Set paid-search and coupon-code rules before the program launches, not after the first invoice.",
    ],
    redFlags: [
      "An affiliate program with no content or claims standard at all.",
      "Disclosure appearing only where a reader would have to go looking for it.",
      "No right to require removal or edits of partner content.",
      "No monitoring of any kind.",
      "Affiliates making health or outcome claims about an ingestible product.",
      "Commission paid on traffic driven by bidding on your own brand name.",
    ],
    beforeYouSign: [
      "The claims your own marketing is permitted to make, written down, so affiliates can be held to the same list.",
      "Your affiliate program terms, with the disclosure and claims standard actually in them.",
      "A sample of what your current partners are publishing today.",
    ],
    related: [
      "influencer-disclosure",
      "affiliate-agreement",
      "influencer-affiliate-agreement",
      "referral-agreement",
      "website-terms-of-use",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "influencer-disclosure",
    name: "Influencer Disclosure",
    category: "ecommerce",
    subcategory: "Disclosures",
    aliases: [
      "Sponsored Content Disclosure",
      "Paid Partnership Disclosure",
      "Creator Disclosure Standard",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Creator",
    whenYouNeedIt:
      "Before you send free product to anyone with an audience, and before any paid creator post, gifting campaign, ambassador program or whitelisted ad goes live.",
    whatItDoes:
      "Sets the disclosure standard for creator content about your product: what has to be said, where it has to appear, on which platform, when, and what happens if it is missing.",
    whyItMatters:
      "Gifted product is a paid relationship, and founders consistently treat it as though it were not. The exposure doubles the moment you turn creator content into an ad: a post that was thin on disclosure becomes advertising you are running with your own money and your own name on it. Expectations vary by platform and country and change frequently, so the workable approach is a written standard in every creator agreement, approved copy, and a check before the content runs rather than after.",
    majorTerms: [
      "What counts as a material connection, including gifted product",
      "Required disclosure wording and where it must appear",
      "Platform-specific placement expectations",
      "Timing: before the audience engages, not at the end of a caption",
      "Whether the platform's own paid-partnership tool must also be used",
      "Review or approval of content before posting",
      "Prohibited claims list",
      "Rules for reposting, whitelisting and paid amplification",
      "Archiving of live content",
      "Consequences of non-disclosure",
      "Application to employees, ambassadors and friends of the brand",
    ],
    clausesToWatch: [
      {
        clause: "Whether gifted-only relationships are covered",
        why: "Brands routinely assume disclosure applies to paid posts only, which leaves the largest part of the program uncovered.",
      },
      {
        clause: "Placement of the disclosure",
        why: "A disclosure at the end of a caption, behind a 'more' cut, or in a comment is where most problems are found.",
      },
      {
        clause: "Whitelisting and paid amplification",
        why: "Once you promote a creator post as an ad it is your advertising, and it has to meet the standard you would apply to your own.",
      },
      {
        clause: "Prohibited claims",
        why: "For an ingestible this matters more than the disclosure itself, because a health claim in a creator's own words is still a claim made to sell your product.",
      },
      {
        clause: "Review before posting",
        why: "Fixing a live post is far more visible, and far more awkward, than approving a draft.",
      },
      {
        clause: "Employee and ambassador posts",
        why: "Staff, ambassadors and friends of the brand posting about the product are in a material relationship too, and rarely told so.",
      },
      {
        clause: "Archiving",
        why: "Without saved copies of what ran you cannot show what was disclosed once a post is edited or deleted.",
      },
    ],
    cpgIssues: [
      "Supplement and functional beverage creators are drawn to outcome language — energy, sleep, gut health, focus — which is exactly what a label review would have removed.",
      "Before-and-after and personal-results content is common in wellness and beauty and raises claim questions well beyond disclosure.",
      "Dosing, stacking and usage advice given casually in a creator video can directly contradict your own label.",
      "Recipe and day-in-the-life formats bury the product mention in editorial content, which is where disclosure most often disappears.",
      "Ambassador programs built on personal discount codes create a continuing material connection needing disclosure on every post, not just the first.",
      "Allergen and ingredient statements made by a creator are frequently wrong, and a correction after the fact is far harder than a pre-post review.",
    ],
    founderMistakes: [
      "Gifting product widely with no written expectations, and building a library of undisclosed content.",
      "Boosting a creator post into a paid ad without checking whether its disclosure and claims would survive as the brand's own advertising.",
      "Sending a claims list to the agency but never to the creators who actually wrote the captions.",
      "Keeping no archive of posts, so nothing could be shown once content was deleted.",
      "Letting employees and ambassadors post with no disclosure standard at all.",
      "Approving a post describing a health outcome the brand had deliberately kept off its own packaging.",
    ],
    negotiationPoints: [
      "Put the disclosure standard and the prohibited-claims list in every creator agreement, with example wording.",
      "Require content review before it goes live, and build the timeline to allow it.",
      "Apply the same standard to gifted, ambassador and employee content as to paid posts.",
      "Require the platform's paid-partnership tool as well as in-caption disclosure.",
      "Archive every post you paid for or gifted against, including stories.",
      "Reserve the right to require edits, withhold payment and stop using content that does not comply.",
    ],
    redFlags: [
      "A gifting program with no written expectations of any kind.",
      "Disclosure appearing only after a 'more' cut or in a comment.",
      "Creator content amplified as advertising with no claims check.",
      "No prohibited-claims list for an ingestible product.",
      "No archive of what actually ran.",
      "Ambassadors on ongoing code arrangements posting with no disclosure at all.",
    ],
    beforeYouSign: [
      "Your written claims list, agreed internally, before any creator sees a brief.",
      "The creator agreement you use, with the disclosure standard actually in it.",
      "An archive process for capturing posts and stories as they run.",
      "A decision on whether content will be amplified as paid advertising, since that raises the standard.",
    ],
    related: [
      "influencer-agreement",
      "ugc-creator-agreement",
      "content-creator-agreement",
      "brand-ambassador-agreement",
      "affiliate-disclosure",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "cookie-policy",
    name: "Cookie Policy",
    category: "ecommerce",
    subcategory: "Privacy and data",
    aliases: [
      "Cookie Notice",
      "Tracking Technologies Policy",
      "Cookie Consent Notice",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Website Visitor",
    whenYouNeedIt:
      "As soon as the site runs anything beyond the platform's own essential functions: an analytics tag, an advertising pixel, a chat widget, a review app, a heatmap tool.",
    whatItDoes:
      "Describes the tracking technologies your site actually uses, what each does, who receives the data, and how a visitor can control them. It works alongside whatever consent banner you run.",
    whyItMatters:
      "Most CPG storefronts accumulate an advertising and analytics stack the way a garage accumulates tools: one at a time, each added by a different person for a different campaign, none ever removed. A policy listing four tools while the site loads a dozen is a public statement that does not match reality, and the banner is very often configured to block nothing at all — which is worse than having no banner, because it claims to offer a choice that was never real. What must be disclosed and consented to varies by country and state and changes, so both the policy and the banner belong with counsel.",
    majorTerms: [
      "An inventory of the tracking technologies actually in use",
      "The purpose of each category",
      "Third parties receiving the data",
      "How long each technology persists",
      "The consent mechanism and how choices are stored",
      "Whether tags load before consent is given",
      "How a visitor changes or withdraws consent",
      "Relationship to your privacy policy",
      "Regional differences in how the banner behaves",
      "Who re-runs the audit when tools change",
    ],
    clausesToWatch: [
      {
        clause: "Whether the policy matches the actual tag list",
        why: "An inaccurate inventory is the most common problem here and also the easiest one to check.",
      },
      {
        clause: "Whether tags fire before consent",
        why: "A banner that is decorative while the pixels have already loaded claims a choice the visitor never actually had.",
      },
      {
        clause: "Withdrawal of consent",
        why: "If there is no way for a visitor to change their mind, the choice you offered only ever ran one direction.",
      },
      {
        clause: "Third-party recipients",
        why: "Advertising and analytics tools pass data onward, and the policy has to describe that rather than simply list a product name.",
      },
      {
        clause: "Regional behaviour of the banner",
        why: "Brands shipping internationally usually run one banner configuration everywhere, and expectations are not the same everywhere.",
      },
      {
        clause: "Review process when marketing adds a tool",
        why: "Tags get added by whoever is running the campaign, and nothing in that process prompts anyone to update the policy.",
      },
    ],
    cpgIssues: [
      "Quiz funnels used to recommend a supplement or a regimen carry tracking alongside answers people treat as health information.",
      "Retargeting cart abandoners is standard practice for a consumable, and it is exactly the kind of tracking a visitor expects to be told about.",
      "Customer-list matching with ad platforms links purchase history to advertising profiles, which is more than a cookie question and belongs in the same review.",
      "Review, loyalty and referral apps common on CPG storefronts each add their own tracking, usually invisibly to the founder who installed them.",
      "Subscription platforms and their analytics observe purchase cadence and payment behaviour over years, not sessions.",
      "A single theme change or app install can add tags nobody recorded, so the inventory has to be re-run rather than assumed to hold.",
    ],
    founderMistakes: [
      "Installing a consent banner that blocked nothing while every pixel loaded on the first page view.",
      "Listing three tools in the policy while the site loaded a dozen.",
      "Adding apps and pixels for a year without re-checking the policy once.",
      "Running one banner configuration across every country they shipped to.",
      "Copying a cookie policy from a brand whose stack was entirely different.",
    ],
    negotiationPoints: [
      "Run a real tag audit on the live site and build the policy from what you find.",
      "Ask your developer to confirm what loads before consent and what does not.",
      "Ask counsel which regions your banner needs to behave differently in.",
      "Give one person ownership of re-running the audit whenever an app or pixel is added.",
      "Keep the cookie policy and the privacy policy consistent about the same tools.",
    ],
    redFlags: [
      "A banner that does not actually control anything.",
      "A policy that does not match the live tag list.",
      "No way to withdraw consent once it has been given.",
      "One banner behaviour applied to every country you ship to.",
      "A policy copied from another brand's site.",
      "No owner for keeping the tool inventory current.",
    ],
    beforeYouSign: [
      "A tag audit of the live site, run by someone who can see what actually loads.",
      "A list of every app and integration installed on the storefront.",
      "The states and countries you serve, so the banner behaviour can be set deliberately.",
      "Your privacy policy, so the two documents describe the same tools in the same terms.",
    ],
    related: [
      "privacy-policy",
      "website-terms-of-use",
      "email-marketing-consent",
      "sms-marketing-terms",
      "website-development-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "What must be disclosed and consented to for tracking technologies varies by country and state and changes often, and the banner configuration matters at least as much as the policy text.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default ECOMMERCE_DOCS;
